import { OrderForm } from '../../models/orderForm';

/**
 * Shape of a single entry in the `upsells` array sent to the TouchCR order API.
 *
 * `withInstallments` is omitted entirely when the offer is not an installments
 * offer, which mirrors how OrderProvider.createBody() builds its `funnels`
 * entries for the initial order.
 */
export interface UpsellPayloadItem {
  sfid?: string;
  count: number;
  withInstallments?: boolean;
}

/** One `upsells` payload entry, tagged `withInstallments: true` when the offer allows it. */
export function buildUpsellPayloadItem(upsell?: Partial<OrderForm> | null): UpsellPayloadItem {
  return {
    sfid: upsell?.sfid,
    count: 1,
    ...(upsell?.installmentsAvailable ? { withInstallments: true } : {}),
  };
}

/** Installment terms of the offer that sold an upsell, as recorded on the placed order. */
export interface PurchasedUpsellInstallments {
  initialInstallmentAmount: number;
  totalInstallments: number;
}

/** Recording upsells installments */
export function recordUpsellInstallments(order: any, upsell?: Partial<OrderForm> | null): any {
  const initialAmount = Number(upsell?.initialInstallmentAmount);
  if (!order || !upsell?.installmentsAvailable || !initialAmount) {
    return order;
  }

  order.purchasedInstallments = {
    initialInstallmentAmount: initialAmount,
    totalInstallments: Number(upsell?.totalInstallments) || 1,
  } as PurchasedUpsellInstallments;
  return order;
}

/**
 * Amount to charge today. Installments upsells are billed Salesforce's
 * `initialInstallmentAmount` plus tax and shipping; otherwise the full order total.
 * Digital offers pass 0 shipping; physical offers pass the offer/order shipping cost.
 */
export function getUpsellChargeAmount(
  upsell: Partial<OrderForm> | null | undefined,
  orderTotal: number,
  orderTax: number,
  shippingCost: number = 0,
): number {
  const initialAmount = Number(upsell?.initialInstallmentAmount);
  if (!upsell?.installmentsAvailable || !initialAmount) {
    return orderTotal;
  }
  return initialAmount + (orderTax || 0) + (shippingCost || 0);
}
